/**
 * Shared geo zone helpers, moved from pages/targets/EstablishmentForm.tsx (lot B step 1).
 *
 * Contains:
 *   - countryFlag helper
 *   - ZoneTypeIcon — granularity icon for a geo zone
 */
import PublicIcon from '@mui/icons-material/Public';
import TerrainIcon from '@mui/icons-material/Terrain';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import HomeIcon from '@mui/icons-material/Home';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import PinDropIcon from '@mui/icons-material/PinDrop';

// ---------------------------------------------------------------------------
// countryFlag helper
// ---------------------------------------------------------------------------

/** ISO-3166-1 alpha-2 → flag emoji (e.g. "FR" → "🇫🇷"). */
export function countryFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  return code.toUpperCase().replace(/./g, (c) =>
    String.fromCodePoint(c.charCodeAt(0) + 127397),
  );
}

// ---------------------------------------------------------------------------
// ZoneTypeIcon
// ---------------------------------------------------------------------------

/**
 * Icon for the zone granularity — based on `type` only.
 * Note: Geoapify populates `address` for ALL result types (it's just the
 * formatted display string), so we NEVER use the presence of `address` to
 * infer street-level granularity — `type` is the only reliable signal.
 */
export function ZoneTypeIcon({ zone, size = 14 }: { zone?: any; size?: number }) {
  const sx = { fontSize: size, flexShrink: 0, color: 'text.disabled' };
  const type: string = zone?.type ?? '';
  if (type === 'country')                        return <PublicIcon sx={sx} />;       // 🌍
  if (type === 'region')                         return <TerrainIcon sx={sx} />;      // ⛰️
  if (type === 'postcode')                       return <LocalPostOfficeIcon sx={sx} />; // 📫
  if (type === 'address' || type === 'building') return <HomeIcon sx={sx} />;         // 🏠
  if (type === 'city')                           return <LocationCityIcon sx={sx} />; // 🏙️ city skyline
  if (type === 'village')                        return <HolidayVillageIcon sx={sx} />; // 🏘️ houses
  if (type === 'locality' || type === 'district' || type === 'suburb') return <ApartmentIcon sx={sx} />; // 🏢 neighborhood
  if (type)                                      return <ApartmentIcon sx={sx} />;
  return <PinDropIcon sx={sx} />;
}
